'use client';

import { useEffect, useRef } from 'react';
import Link from 'next/link';

const WEB_APP_URL = 'https://app.getsummons.com';

interface FinalCTASectionProps {
  onOpenModal: () => void;
}

export default function FinalCTASection({ onOpenModal }: FinalCTASectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef.current?.querySelectorAll('.reveal, .reveal-scale');
    elements?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const trackEvent = (event: string) => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', event);
    }
  };

  return (
    <section ref={sectionRef} className="py-20 lg:py-28 px-4 sm:px-6 bg-foreground relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-accent/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-highlight/8 blur-3xl" />
      </div>

      <div className="max-w-4xl mx-auto text-center relative z-10">
        <h2 className="text-5xl sm:text-6xl lg:text-7xl font-black text-white tracking-tight leading-tight mb-6 reveal" style={{ transitionDelay: '0.05s' }}>
          Summon Your
          <br />
          <span className="text-primary">First Genie.</span>
        </h2>

        <p className="text-xl text-white/60 mb-10 max-w-xl mx-auto leading-relaxed reveal" style={{ transitionDelay: '0.1s' }}>
          Your Wish, Our Command. Be first in line when we launch — join the waitlist today.
        </p>

        {/* Join Waitlist CTA */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8 reveal" style={{ transitionDelay: '0.15s' }}>
          <Link
            href="/waitlist"
            onClick={() => trackEvent('click_join_waitlist_final_cta')}
            className="inline-flex items-center justify-center gap-2 bg-primary text-white px-10 py-4 rounded-2xl font-bold text-lg hover:bg-primary/90 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 active:translate-y-0"
          >
            <span>Join the Waitlist</span>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          </Link>
        </div>

        {/* Coming Soon Store Badges */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8 reveal" style={{ transitionDelay: '0.2s' }}>
          <div className="flex items-center justify-center gap-3 bg-white text-foreground px-7 py-4 rounded-2xl font-semibold text-sm opacity-60 cursor-not-allowed select-none">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="#0A0A0A"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
            <div className="text-left">
              <div className="text-[10px] opacity-50 font-normal">Coming Soon to the</div>
              <div className="text-base font-bold leading-tight">App Store</div>
            </div>
          </div>

          <div className="flex items-center justify-center gap-3 bg-gray-600 text-white px-7 py-4 rounded-2xl font-semibold text-sm opacity-60 cursor-not-allowed select-none">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="white"><path d="M17.523 15.3414c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4483.9993.9993.0001.5511-.4482.9997-.9993.9997m-11.046 0c-.5511 0-.9993-.4486-.9993-.9997s.4482-.9993.9993-.9993c.5511 0 .9993.4483.9993.9993 0 .5511-.4482.9997-.9993.9997m11.4045-6.02l1.9973-3.4592a.416.416 0 00-.1521-.5676.416.416 0 00-.5676.1521l-2.0223 3.503C15.5902 8.2439 13.8533 7.8508 12 7.8508s-3.5902.3931-5.1367 1.0989L4.841 5.4467a.4161.4161 0 00-.5677-.1521.4157.4157 0 00-.1521.5676l1.9973 3.4592C3.8592 10.3902 2.3 12.3 2 14.6h20c-.3-2.3-1.8592-4.2098-4.1185-5.2786"/></svg>
            <div className="text-left">
              <div className="text-[10px] opacity-70 font-normal">Coming Soon on</div>
              <div className="text-base font-bold leading-tight">Google Play</div>
            </div>
          </div>
        </div>

        {/* Web App link */}
        <div className="reveal" style={{ transitionDelay: '0.25s' }}>
          <a
            href={WEB_APP_URL}
            onClick={() => trackEvent('click_use_web')}
            className="text-white/40 text-sm hover:text-white/70 transition-colors underline underline-offset-4"
          >
            Or use the web app at app.getsummons.com →
          </a>
        </div>

        {/* Trust signals */}
        <div className="flex flex-wrap items-center justify-center gap-6 mt-12 pt-10 border-t border-white/10 reveal" style={{ transitionDelay: '0.3s' }}>
          {[
            { icon: '🔒', text: 'Escrow Protected' },
            { icon: '⚡', text: 'Instant Matching' },
            { icon: '📱', text: 'iOS & Android' },
            { icon: '🆓', text: 'Free to Use' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-white/40 text-xs font-medium">
              <span>{item.icon}</span>
              {item.text}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}